import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UnderwritingPromptStatus,
  UnderwritingPromptVersion,
  wizardUnderwritingGuidelinesApi,
  wizardUnderwritingGuidelinesKeys,
} from '../api/api';

export function useUnderwritingPromptVersions(params?: {
  promptKey?: string;
  status?: UnderwritingPromptStatus;
}) {
  return useQuery({
    queryKey: wizardUnderwritingGuidelinesKeys.versions(params),
    queryFn: () => wizardUnderwritingGuidelinesApi.listVersions(params),
  });
}

export function useUnderwritingPromptVersion(versionId: string | null | undefined) {
  return useQuery({
    queryKey: wizardUnderwritingGuidelinesKeys.version(versionId ?? ''),
    queryFn: () => wizardUnderwritingGuidelinesApi.getVersion(versionId!),
    enabled: !!versionId,
  });
}

export function useSaveUnderwritingPromptDraft() {
  const qc = useQueryClient();
  return useMutation<UnderwritingPromptVersion, Error, { promptKey: string; content: string }>({
    mutationFn: data => wizardUnderwritingGuidelinesApi.saveDraft(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wizardUnderwritingGuidelinesKeys.all });
    },
  });
}

export function useUpdateUnderwritingPromptDraft() {
  const qc = useQueryClient();
  return useMutation<
    UnderwritingPromptVersion,
    Error,
    { draftId: string; content: string }
  >({
    mutationFn: ({ draftId, content }) =>
      wizardUnderwritingGuidelinesApi.updateDraft(draftId, { content }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wizardUnderwritingGuidelinesKeys.all });
    },
  });
}

export function useDiscardUnderwritingPromptDraft() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { draftId: string }>({
    mutationFn: ({ draftId }) => wizardUnderwritingGuidelinesApi.discardDraft(draftId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wizardUnderwritingGuidelinesKeys.all });
    },
  });
}

export function usePublishUnderwritingPromptDraft() {
  const qc = useQueryClient();
  return useMutation<
    UnderwritingPromptVersion,
    Error,
    { draftId: string; notes: string }
  >({
    mutationFn: ({ draftId, notes }) =>
      wizardUnderwritingGuidelinesApi.publishDraft(draftId, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wizardUnderwritingGuidelinesKeys.all });
    },
  });
}
